// «QUIÉN NO ACUMULA» (F1, 19-oct-2026 — petición #1465 de Paco, Silvestre): empleados, influencers o cuentas de
// prueba que pueden usar el kiosco pero NO suman visitas (la visita se registra en 0) ni canjean (saldo congelado).
// Backend: lealify-base routes/loyalty.js (/accrual-blocks, /contacts/:id/accrual-block) + lib/accrualBlocks.js.
// Diseño: forobeta-ops/docs/lealify-excluidos-visitas-ux-20261019.md
import { useEffect, useState } from 'react';
import { baseApi } from './baseClient';

export type AccrualReason = 'empleado' | 'influencer' | 'otro';
export const REASON_LABEL: Record<AccrualReason, string> = { empleado: 'Empleado', influencer: 'Influencer', otro: 'Otro' };

export interface AccrualBlock {
  id: number;
  contact_id: number;
  reason: AccrualReason;
  reason_label: string;
  note: string | null;
  starts_at: string;
  ends_on: string | null;      // «hasta» inclusivo (YYYY-MM-DD), null = hasta que lo quiten
  source: 'manual' | 'pos_staff' | 'team';
  created_by_name: string | null;
  active: boolean;
  first_name?: string;
  last_name?: string | null;
  phone?: string | null;
}
export interface AccrualBlocksList {
  blocks: AccrualBlock[];
  ticket_lock: { enabled: boolean; window_min: number };
}

// Caché compartida (lista vigente del negocio, pequeña): la usan los chips de Lista de clientes y Visitas por QR.
let cache: { at: number; data: AccrualBlocksList } | null = null;
let inflight: Promise<AccrualBlocksList> | null = null;
const listeners = new Set<() => void>();
const TTL = 60_000;
const notify = () => listeners.forEach((fn) => fn());

export const accrualBlocksApi = {
  list: async (force = false): Promise<AccrualBlocksList> => {
    if (!force && cache && Date.now() - cache.at < TTL) return cache.data;
    if (!force && inflight) return inflight;
    inflight = baseApi.get<AccrualBlocksList>('/loyalty/accrual-blocks')
      .then((d) => { cache = { at: Date.now(), data: d }; notify(); return d; })
      .finally(() => { inflight = null; });
    return inflight;
  },
  forContact: (contactId: string | number) =>
    baseApi.get<{ block: AccrualBlock | null }>(`/loyalty/contacts/${contactId}/accrual-block`).then((r) => r.block),
  save: async (contactId: string | number, body: { reason: AccrualReason; note?: string | null; ends_on?: string | null }) => {
    const r = await baseApi.put<{ block: AccrualBlock }>(`/loyalty/contacts/${contactId}/accrual-block`, body);
    cache = null; void accrualBlocksApi.list(true).catch(() => {});
    return r.block;
  },
  remove: async (contactId: string | number) => {
    await baseApi.del<{ ok: boolean }>(`/loyalty/contacts/${contactId}/accrual-block`);
    cache = null; void accrualBlocksApi.list(true).catch(() => {});
  },
};

// Mapa contact_id → bloqueo vigente, para pintar el chip «No acumula» en listas. Si falla, mapa vacío (sin chips).
export function useAccrualBlockMap(): Map<string, AccrualBlock> {
  const [map, setMap] = useState<Map<string, AccrualBlock>>(() => toMap(cache?.data));
  useEffect(() => {
    let vivo = true;
    const fn = () => { if (vivo) setMap(toMap(cache?.data)); };
    listeners.add(fn);
    accrualBlocksApi.list().then(fn).catch(() => {});
    return () => { vivo = false; listeners.delete(fn); };
  }, []);
  return map;
}
function toMap(d?: AccrualBlocksList): Map<string, AccrualBlock> {
  return new Map((d?.blocks || []).map((b) => [String(b.contact_id), b]));
}

// «hasta 31 dic» (o «31 dic 2027» si es de otro año).
export function fmtHasta(ymd: string | null): string {
  if (!ymd) return 'sin fecha';
  const [y, m, d] = ymd.split('-').map(Number);
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `hasta ${d} ${meses[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
}
